import { useCallback, useEffect, useState } from 'react'
import IssuesService from '../../../services/api/IssuesService'

const INITIAL_PAGY = { page: 1, pages: 0, count: 0, items: 0 }

const useTriggeredAlerts = (collectId, {
  page, items, search, issueStatus, issueContent, sort, direction, draftId, columns, variables,
  startDate, endDate
}) => {
  const [issues, setIssues] = useState([])
  const [pagy, setPagy] = useState(INITIAL_PAGY)
  const [loading, setLoading] = useState(true)
  const [hasLoaded, setHasLoaded] = useState(false)
  const [error, setError] = useState(null)
  const [refetchToken, setRefetchToken] = useState(0)

  const refetch = useCallback(() => setRefetchToken(token => token + 1), [])
  // Surgical row merge so mutation callers reconcile a single issue without a full
  // Refetch (which would flicker the UI). Merge, not replace: a status/attribution
  // PATCH response omits the custom-column values (extra_values / extra_variable_values),
  // Which never change on such a mutation — keep the ones already loaded.
  const mergeIssue = useCallback(updated => {
    if (!updated?.id)
      return
    setIssues(previous => previous.map(issue => {
      if (issue.id !== updated.id)
        return issue
      return { ...issue, ...updated }
    }))
  }, [])
  const columnsKey = columns ? columns.join(',') : ''
  const variablesKey = variables ? variables.join(',') : ''

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)

    const requestParams = {
      page,
      items,
      search,
      issueStatus,
      issueContent,
      sort,
      direction,
      draftId,
      columns,
      variables,
      startDate,
      endDate
    }
    IssuesService.getIssues(collectId, requestParams)
      .then(response => {
        if (cancelled)
          return
        setIssues(response.issues || [])
        setPagy(response.pagy || INITIAL_PAGY)
      })
      .catch(err => {
        if (cancelled)
          return
        setError(err)
        setIssues([])
        setPagy(INITIAL_PAGY)
      })
      .finally(() => {
        if (cancelled)
          return
        setLoading(false)
        setHasLoaded(true)
      })

    return () => {
      cancelled = true
    }
    // Stable string keys avoid re-firing on `columns`/`variables` array reference churn.
  }, [
    collectId,
    page,
    items,
    search,
    issueStatus,
    issueContent,
    sort,
    direction,
    draftId,
    columnsKey,
    variablesKey,
    startDate,
    endDate,
    refetchToken
  ])

  return { issues, pagy, loading, hasLoaded, error, refetch, mergeIssue }
}

export default useTriggeredAlerts
